// =============================================================================
// bookingPaymentStatus.ts — shared booking payment/colour predicates
//
// Pure helpers used by BOTH the V1 grid cell (facility/BookingCell) and the V2
// grid cell (v2/bookingStatus). Kept in the bookings root — above both the
// `facility/` (V1) and `v2/` trees — so the "zero-value admin booking" rule has
// a single source of truth and the two screens cannot drift apart.
// =============================================================================

import { Slot } from '../../model/bookings/Slot';
import { Booking } from '../../model/bookings/Booking';

/**
 * Is this an "admin booking" — one the club placed itself (staff or a coach)
 * rather than a regular member's personal booking?
 *
 * Such bookings carry the `NonMembers` flag and/or a `BookingTitle`. They are
 * attributed to the club "house" member (when staff placed them) OR to the coach
 * who placed them (a coach books as themselves so the session is traceable), so the
 * reliable signal is the metadata — NOT the member name. The legacy
 * member-name-equals-client check is kept as a fallback for older house bookings.
 *
 * NB: `currentClientId` carries the client *name* (that is what ClientProvider
 * stores), so comparing it to `memberName` is the right legacy check. `NonMembers`
 * arrives as a string ('true'/'false') from the grid adapter, so coerce to string
 * before comparing. [FORT-278]
 */
export function isAdminBooking(
  slot: Slot,
  booking: Booking | null,
  currentClientId?: string
): boolean {
  if (!booking) return false;
  if (currentClientId && booking.memberName === currentClientId) return true;
  if (String(booking.metadata?.NonMembers).toLowerCase() === 'true') return true;
  return !!slot.metadata?.BookingTitle || !!booking.metadata?.BookingTitle;
}

/**
 * Is this a zero-value admin booking — an admin booking with nothing to pay?
 *
 * True when the booking is an admin booking AND its transaction amount is 0 or
 * absent (no transaction). These are treated as fully settled: rendered green
 * (booked), never pending/overdue, and they carry no payment tag.
 *
 * NB: use `== null || === 0`, NOT `!amount` — a genuine £0 must count as
 * zero-value, and a missing amount (null) means no transaction either way.
 */
export function isZeroValueAdminBooking(
  slot: Slot,
  booking: Booking | null,
  currentClientId?: string
): boolean {
  if (!isAdminBooking(slot, booking, currentClientId)) return false;
  const amount = booking?.transactionAmount;
  return amount == null || amount === 0;
}
